(function () {
  const DEFAULT_MOBILE_MAX = 989;

  let syncing = false;
  let mainSectionId = null;
  let stickySectionId = null;
  let stickyBar = null;
  let scrollObserver = null;
  let scrollRevealHandler = null;
  let revealTarget = null;
  /** @type {'top' | 'past_primary' | null} */
  let revealStrategy = null;
  /** @type {Element[]} */
  let primaryCtaTargets = [];
  let kachingRevealObserver = null;
  let primaryRebindTimer = null;
  let menuObserver = null;
  let pickerObserver = null;
  let rubikObserver = null;
  let menuOpen = false;
  let wasVisibleBeforeMenu = false;
  let galleryModalOpen = false;
  let galleryModalObserver = null;
  let repairingPicker = false;
  let cachedBarHeight = 0;

  function isMobileViewport(maxWidth) {
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  }

  function getMainSectionId() {
    const el = document.querySelector('[id^="MainProduct-"][data-section]');
    return el?.dataset?.section || null;
  }

  function getStickyBar() {
    return document.querySelector('.plumelle-sticky-atc[data-section-id]');
  }

  function getStickySectionId() {
    return stickyBar?.dataset?.sectionId || null;
  }

  function getMobileMaxWidth() {
    return DEFAULT_MOBILE_MAX;
  }

  function uniquifyStickyRadioNames(picker) {
    if (!picker || !stickySectionId) return;

    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (input.name && !input.name.includes('-sticky-')) {
        input.name = `${input.name}-sticky-${stickySectionId}`;
      }

      const formId = `product-form-${stickySectionId}`;
      if (input.getAttribute('form')) {
        input.setAttribute('form', formId);
      }
    });
  }

  function getRubikShadowRoot() {
    return document.querySelector('rubik-swatch')?.shadowRoot || null;
  }

  function getRubikColorOption(shadowRoot = getRubikShadowRoot()) {
    if (!shadowRoot) return null;

    for (const option of shadowRoot.querySelectorAll('.rubik-swatch__option')) {
      if (option.querySelector('.rubik-swatch__label--image')) {
        return option;
      }
    }

    return null;
  }

  function getMainColorFieldset() {
    const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
    if (!mainPicker) return null;

    const markedFieldset = mainPicker.querySelector('[data-is-variant-image-swatch="true"]');
    if (markedFieldset) return markedFieldset;

    for (const fieldset of mainPicker.querySelectorAll('[data-option-position]')) {
      if (fieldset.querySelector('label.variant-swatch, label.color-swatch')) {
        return fieldset;
      }
    }

    const rubikColor = getRubikColorOption();
    if (rubikColor?.dataset?.rubikOptionIndex) {
      const fieldset = mainPicker.querySelector(
        `[data-option-position="${rubikColor.dataset.rubikOptionIndex}"]`
      );
      if (fieldset) return fieldset;
    }

    return mainPicker.querySelector('[data-option-position="1"]');
  }

  function syncColorSwatchesFromRubik() {
    const rubikColor = getRubikColorOption();
    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!rubikColor || !stickyPicker) return false;

    const position = rubikColor.dataset.rubikOptionIndex;
    if (!position) return false;

    const stickyFieldset = stickyPicker.querySelector(`[data-option-position="${position}"]`);
    if (!stickyFieldset) return false;

    let synced = false;

    rubikColor.querySelectorAll('.rubik-swatch__item-wrapper').forEach((item) => {
      const rubikInput = item.querySelector('.rubik-swatch__input');
      if (!rubikInput) return;

      const valueId = rubikInput.dataset.rubikOptionValueId;
      const value = rubikInput.dataset.rubikOptionValue || rubikInput.value;
      const imageUrl =
        item.querySelector('.rubik-swatch__image')?.currentSrc ||
        item.querySelector('.rubik-swatch__image')?.src ||
        '';

      const stickyInput = stickyFieldset.querySelector(
        valueId
          ? `input[data-option-value-id="${valueId}"]`
          : `input[value="${CSS.escape(value)}"]`
      );

      if (!stickyInput) return;

      const label = stickyInput.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') return;

      label.className = 'variant-swatch bg_size-square focus-inset';
      label.htmlFor = stickyInput.id;
      label.style.backgroundImage = imageUrl ? `url("${imageUrl}")` : '';
      label.textContent = '';

      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = value || '';
      label.appendChild(hidden);

      synced = true;
    });

    if (!synced) return false;

    stickyFieldset.setAttribute('data-is-variant-image-swatch', 'true');
    stickyFieldset.classList.add('product-form__controls--colors');

    const rubikValue = rubikColor.querySelector('.rubik-swatch__option-name-value');
    const colorNameEl = ensureStickyColorNameElement(stickyFieldset);
    if (rubikValue && colorNameEl) {
      colorNameEl.textContent = rubikValue.textContent.trim();
    }

    repairStickyPicker();
    uniquifyStickyRadioNames(stickyPicker);
    return true;
  }

  function getRubikColorOptionLabel() {
    const rubikColor = getRubikColorOption();
    return (
      rubikColor
        ?.querySelector('.rubik-swatch__option-name-label')
        ?.textContent?.replace(':', '')
        .trim() || 'Kleur'
    );
  }

  function ensureStickyColorNameElement(fieldset) {
    if (!fieldset) return null;

    let groupName = fieldset.querySelector('.product-form__group-name');
    const controlsGroup = fieldset.querySelector('.product-form__controls-group');

    if (!groupName && controlsGroup) {
      groupName = document.createElement('div');
      groupName.className = 'product-form__group-name subtitle';
      fieldset.insertBefore(groupName, controlsGroup);
    }

    if (!groupName) return null;

    let colorNameEl = groupName.querySelector('.product-options-name');
    if (!colorNameEl) {
      const labelText = groupName.textContent.trim() || getRubikColorOptionLabel();
      groupName.textContent = '';

      groupName.append(`${labelText}: `);

      colorNameEl = document.createElement('span');
      colorNameEl.className = 'product-options-name';
      groupName.appendChild(colorNameEl);
    }

    return colorNameEl;
  }

  function findRubikInputForOption(shadowRoot, position, optionValue, optionValueId) {
    if (!shadowRoot) return null;

    if (optionValueId) {
      const byId = shadowRoot.querySelector(
        `input.rubik-swatch__input[data-rubik-option-position="${position}"][data-rubik-option-value-id="${optionValueId}"]`
      );
      if (byId) return byId;
    }

    if (!optionValue) return null;

    const inputs = shadowRoot.querySelectorAll(
      `input.rubik-swatch__input[data-rubik-option-position="${position}"]`
    );

    for (const input of inputs) {
      if (
        input.value === optionValue ||
        input.dataset.rubikOptionValue === optionValue
      ) {
        return input;
      }
    }

    return null;
  }

  function activateRubikInput(shadowRoot, input) {
    if (!shadowRoot || !input || input.disabled || input.checked) return false;

    const fieldset = input.closest('fieldset');
    fieldset?.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
    });
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function syncRubikFromMainPicker() {
    const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
    const shadowRoot = getRubikShadowRoot();
    if (!mainPicker || !shadowRoot) return false;

    let changed = false;

    mainPicker.querySelectorAll('[data-option-position]').forEach((fieldset) => {
      const position = fieldset.dataset.optionPosition;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!position || !checked) return;

      const targetInput = findRubikInputForOption(
        shadowRoot,
        position,
        checked.value,
        checked.dataset.optionValueId
      );

      if (activateRubikInput(shadowRoot, targetInput)) {
        changed = true;
      }
    });

    return changed;
  }

  function scheduleRubikSyncFromMain() {
    [0, 50, 200, 500].forEach((delay) => {
      window.setTimeout(() => {
        if (syncing) return;
        syncRubikFromMainPicker();
      }, delay);
    });
  }

  function syncStickyAvailabilityFromMain() {
    const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!mainPicker || !stickyPicker) return;

    mainPicker.querySelectorAll('[data-option-position]').forEach((mainFieldset) => {
      const position = mainFieldset.dataset.optionPosition;
      const stickyFieldset = stickyPicker.querySelector(`[data-option-position="${position}"]`);
      if (!stickyFieldset) return;

      mainFieldset.querySelectorAll('input[type="radio"]').forEach((mainInput) => {
        const stickyInput = stickyFieldset.querySelector(
          mainInput.dataset.optionValueId
            ? `input[data-option-value-id="${mainInput.dataset.optionValueId}"]`
            : `input[value="${CSS.escape(mainInput.value)}"]`
        );

        if (!stickyInput) return;

        stickyInput.classList.toggle('disabled', mainInput.classList.contains('disabled'));
        stickyInput.disabled = mainInput.disabled;
      });
    });
  }

  function scheduleStickySyncFromMain(variant) {
    [150, 450, 900].forEach((delay) => {
      window.setTimeout(() => {
        if (syncing) return;

        const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
        const currentVariant = mainPicker?.currentVariant || variant;
        if (!currentVariant) return;

        syncing = true;
        try {
          syncStickyAvailabilityFromMain();
          syncPickerFromVariant(stickySectionId, currentVariant, false);
          syncStickyColorName(currentVariant);
          syncStickyPriceFromMain();
          syncFormState(stickySectionId, currentVariant);
          updateStickyImage(currentVariant);
        } finally {
          syncing = false;
        }
      }, delay);
    });
  }

  function patchStickyPickerRender() {
    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!stickyPicker || stickyPicker.__plumelleRenderPatched) return;

    const originalRender = stickyPicker.renderProductInfo?.bind(stickyPicker);
    if (!originalRender) return;

    stickyPicker.renderProductInfo = function (requestUrl) {
      const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
      if (mainPicker?.renderProductInfo) {
        return mainPicker.renderProductInfo(requestUrl);
      }

      return originalRender(requestUrl);
    };

    stickyPicker.__plumelleRenderPatched = true;
  }

  function syncRubikSwatchStyles() {
    const shadowRoot = getRubikShadowRoot();
    const variantsEl = stickyBar?.querySelector('[data-plumelle-sticky-variants]');

    if (!shadowRoot || !variantsEl) return false;

    let synced = false;
    const imageLabel = shadowRoot.querySelector('.rubik-swatch__label--image');
    const checkedLabel = shadowRoot.querySelector('input:checked + .rubik-swatch__label--pill');
    const uncheckedLabel = shadowRoot.querySelector('input:not(:checked) + .rubik-swatch__label--pill');

    if (imageLabel) {
      const rect = imageLabel.getBoundingClientRect();
      const size = `${Math.round(rect.width || rect.height)}px`;
      variantsEl.style.setProperty('--rubik-swatch-image-size', size);
      synced = true;
    }

    if (uncheckedLabel) {
      const style = getComputedStyle(uncheckedLabel);
      variantsEl.style.setProperty('--rubik-swatch-pill-background', style.backgroundColor);
      variantsEl.style.setProperty('--rubik-swatch-pill-color', style.color);
      variantsEl.style.setProperty('--rubik-swatch-pill-border-color', style.borderColor);
      variantsEl.style.setProperty('--rubik-swatch-pill-padding', style.padding);
      variantsEl.style.setProperty('--rubik-swatch-pill-font-size', style.fontSize);
      synced = true;
    }

    if (checkedLabel) {
      const style = getComputedStyle(checkedLabel);
      variantsEl.style.setProperty('--rubik-swatch-pill-selected-background', style.backgroundColor);
      variantsEl.style.setProperty('--rubik-swatch-pill-selected-color', style.color);
      variantsEl.style.setProperty('--rubik-swatch-pill-selected-border-color', style.borderColor);
      synced = true;
    }

    if (synced) {
      updatePagePadding(true);
    }

    return synced;
  }

  function syncColorSwatchesFromMain() {
    if (!mainSectionId || !stickySectionId) return false;
    if (syncColorSwatchesFromRubik()) return true;

    const mainFieldset = getMainColorFieldset();
    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!mainFieldset || !stickyPicker) return false;

    if (!mainFieldset.querySelector('label.variant-swatch, label.color-swatch')) {
      return false;
    }

    const position = mainFieldset.dataset.optionPosition;
    if (!position) return false;

    const stickyFieldset = stickyPicker.querySelector(`[data-option-position="${position}"]`);
    if (!stickyFieldset) return false;

    stickyFieldset.innerHTML = mainFieldset.innerHTML.replaceAll(mainSectionId, stickySectionId);
    stickyFieldset.className = mainFieldset.className;

    if (mainFieldset.hasAttribute('data-is-variant-image-swatch')) {
      stickyFieldset.setAttribute('data-is-variant-image-swatch', 'true');
    } else {
      stickyFieldset.removeAttribute('data-is-variant-image-swatch');
    }

    repairStickyPicker();
    uniquifyStickyRadioNames(stickyPicker);
    return stickyFieldset.querySelector('label.variant-swatch, label.color-swatch') != null;
  }

  function mountVariantPicker() {
    const container = document.querySelector('[data-plumelle-sticky-variants]');
    if (!container || container.querySelector('variant-radios')) return;
    if (!mainSectionId || !stickySectionId) return;

    const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
    if (!mainPicker) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'product-parameters product-parameters-radius';

    const clone = mainPicker.cloneNode(true);
    clone.id = `variant-radios-${stickySectionId}`;
    clone.dataset.section = stickySectionId;
    clone.innerHTML = mainPicker.innerHTML.replaceAll(mainSectionId, stickySectionId);

    wrapper.appendChild(clone);
    container.appendChild(wrapper);

    uniquifyStickyRadioNames(clone);
    syncColorSwatchesFromMain();
  }

  function linkStickyPickerToMain() {
    if (!mainSectionId || !stickySectionId) return;

    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!stickyPicker) return;

    stickyPicker.dataset.originalSection = mainSectionId;
    uniquifyStickyRadioNames(stickyPicker);
    patchStickyPickerRender();
  }

  function repairStickyPicker() {
    if (repairingPicker) return;

    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!stickyPicker || !mainSectionId || mainSectionId === stickySectionId) return;

    repairingPicker = true;

    try {
      stickyPicker.querySelectorAll(`[id*="${mainSectionId}"]`).forEach((el) => {
        el.id = el.id.replaceAll(mainSectionId, stickySectionId);
      });

      stickyPicker.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.id?.includes(mainSectionId)) {
          input.id = input.id.replaceAll(mainSectionId, stickySectionId);
        }

        const label = input.nextElementSibling;
        if (label?.tagName === 'LABEL' && label.htmlFor?.includes(mainSectionId)) {
          label.htmlFor = label.htmlFor.replaceAll(mainSectionId, stickySectionId);
        }
      });

      uniquifyStickyRadioNames(stickyPicker);
    } finally {
      repairingPicker = false;
    }
  }

  function syncStickyColorName(variant) {
    const stickyPicker = document.getElementById(`variant-radios-${stickySectionId}`);
    if (!stickyPicker) return;

    const colorFieldset = stickyPicker.querySelector('[data-is-variant-image-swatch="true"]');
    if (!colorFieldset) return;

    const colorNameEl = ensureStickyColorNameElement(colorFieldset);
    if (!colorNameEl) return;

    const rubikValue = getRubikColorOption()?.querySelector('.rubik-swatch__option-name-value');
    if (rubikValue?.textContent?.trim()) {
      colorNameEl.textContent = rubikValue.textContent.trim();
      return;
    }

    if (!variant?.options?.length) return;

    const position = Number(colorFieldset.dataset.optionPosition);
    const colorValue = variant.options[position - 1];
    if (colorValue) {
      colorNameEl.textContent = colorValue;
    }
  }

  function applyStickyUpdates(variant, { skipColorSync = false } = {}) {
    if (!skipColorSync) {
      syncColorSwatchesFromMain();
    }

    syncRubikSwatchStyles();
    syncPickerFromVariant(stickySectionId, variant, false);
    repairStickyPicker();
    syncStickyColorName(variant);
    syncStickyPriceFromMain();
    window.setTimeout(syncStickyPriceFromMain, 150);
    syncFormState(stickySectionId, variant);
    updateStickyImage(variant);
  }

  function isMenuOpen() {
    return !!(
      document.querySelector('.burger-menu--open') ||
      document.querySelector('.menu-drawer-container.menu-opening') ||
      document.querySelector('details.menu-drawer-container[open]') ||
      document.body.classList.contains('overflow-hidden-drawer') ||
      document.body.classList.contains('overflow-hidden-tablet')
    );
  }

  function updateMenuState() {
    const open = isMenuOpen();
    if (open === menuOpen) return;

    menuOpen = open;

    if (open) {
      wasVisibleBeforeMenu = stickyBar?.classList.contains('is-visible') ?? false;
      setBarVisible(false);
      return;
    }

    if (wasVisibleBeforeMenu) {
      initRevealMode();
    }
  }

  function observeMenuState() {
    if (menuObserver) {
      menuObserver.disconnect();
      menuObserver = null;
    }

    menuObserver = new MutationObserver(() => updateMenuState());

    menuObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    document.querySelectorAll('burger-menu, header-drawer, .menu-drawer-container').forEach((el) => {
      menuObserver.observe(el, {
        attributes: true,
        attributeFilter: ['class', 'open'],
        subtree: true,
      });
    });

    updateMenuState();
  }

  function observeStickyPickerRepairs() {
    if (pickerObserver) {
      pickerObserver.disconnect();
      pickerObserver = null;
    }

    if (rubikObserver) {
      rubikObserver.disconnect();
      rubikObserver = null;
    }

    const mainFieldset = getMainColorFieldset();
    if (mainFieldset) {
      pickerObserver = new MutationObserver(() => {
        if (syncing) return;
        syncColorSwatchesFromMain();
        syncStickyAvailabilityFromMain();
      });

      pickerObserver.observe(mainFieldset, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    const rubikColor = getRubikColorOption();
    if (rubikColor) {
      rubikObserver = new MutationObserver(() => {
        if (syncing) return;
        syncColorSwatchesFromRubik();
        syncRubikSwatchStyles();
        const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
        if (mainPicker?.currentVariant) {
          syncStickyColorName(mainPicker.currentVariant);
        }
      });

      rubikObserver.observe(rubikColor, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'src'],
      });
    }
  }

  function observeRubikReady() {
    const rubik = document.querySelector('rubik-swatch');
    if (!rubik) return;

    rubik.addEventListener('rubik-swatch-ready', () => {
      syncColorSwatchesFromMain();
      syncRubikSwatchStyles();
      observeStickyPickerRepairs();
    });
  }

  function syncPickerFromVariant(targetSectionId, variant, dispatchChange) {
    const picker = document.getElementById(`variant-radios-${targetSectionId}`);
    if (!picker || !variant?.options?.length) return false;

    let changed = false;

    variant.options.forEach((optionValue, index) => {
      const fieldset = picker.querySelector(`[data-option-position="${index + 1}"]`);
      if (!fieldset) return;

      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (el) => el.value === optionValue
      );

      if (input && !input.checked) {
        input.checked = true;
        changed = true;
        if (dispatchChange) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    return changed;
  }

  function syncStickyPriceFromMain() {
    if (!mainSectionId || !stickySectionId) return;

    const priceSource = document.getElementById(`price-${mainSectionId}`);
    const priceDest = document.getElementById(`price-${stickySectionId}`);

    if (!priceSource || !priceDest) return;

    const sourcePrice = priceSource.querySelector('.price');
    const destPrice = priceDest.querySelector('.price');

    if (sourcePrice && destPrice) {
      destPrice.outerHTML = sourcePrice.outerHTML;
      return;
    }

    priceDest.innerHTML = priceSource.innerHTML;
    priceDest.querySelectorAll('.product__tax').forEach((node) => node.remove());
  }

  function syncFormState(sectionId, variant) {
    if (!variant) return;

    const form = document.getElementById(`product-form-${sectionId}`);
    if (!form) return;

    const idInput = form.querySelector('[name="id"]');
    const submitBtn = form.querySelector('[name="add"]');
    const submitSpan = submitBtn?.querySelector('span');

    if (idInput) {
      idInput.value = variant.id;
      idInput.disabled = !variant.available;
    }

    if (!submitBtn) return;

    if (variant.available) {
      submitBtn.removeAttribute('disabled');
      if (submitSpan) {
        submitSpan.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }
    } else {
      submitBtn.setAttribute('disabled', 'disabled');
      if (submitSpan) {
        submitSpan.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    }
  }

  function updateStickyImage(variant) {
    const img = document.querySelector('.plumelle-sticky-atc__media img');
    if (!img || !variant) return;

    const src =
      variant.featured_image?.src ||
      variant.featured_media?.preview_image?.src ||
      '';

    if (src) {
      img.src = src;
      img.srcset = src;
    }
  }

  function getProductModal() {
    return document.querySelector('product-modal.product-media-modal');
  }

  function isGalleryModalOpen() {
    return getProductModal()?.hasAttribute('open') ?? false;
  }

  function updateGalleryModalPadding(forceRemeasure = false) {
    const dialog = document.querySelector('product-modal[open] .product-media-modal__dialog');
    if (!dialog || !stickyBar) return;

    if (forceRemeasure || !cachedBarHeight) {
      cachedBarHeight = stickyBar.offsetHeight;
    }

    if (!cachedBarHeight) return;

    dialog.style.paddingBottom = `calc(2.4rem + ${cachedBarHeight}px)`;
  }

  function clearGalleryModalPadding() {
    document.querySelectorAll('.product-media-modal__dialog').forEach((dialog) => {
      dialog.style.removeProperty('padding-bottom');
    });
  }

  function updateGalleryMode() {
    const open = isGalleryModalOpen() && isMobileViewport(getMobileMaxWidth());
    if (open === galleryModalOpen) return;

    galleryModalOpen = open;

    if (open) {
      stickyBar?.classList.add('is-gallery-mode');
      document.body.classList.add('plumelle-gallery-atc-active');
      updatePagePadding(true);
      setBarVisible(true);
      updateGalleryModalPadding(true);
      return;
    }

    stickyBar?.classList.remove('is-gallery-mode');
    document.body.classList.remove('plumelle-gallery-atc-active');
    clearGalleryModalPadding();
    initRevealMode();
  }

  function onModalClosed() {
    if (!galleryModalOpen) return;
    updateGalleryMode();
  }

  function observeGalleryModal() {
    if (galleryModalObserver) {
      galleryModalObserver.disconnect();
      galleryModalObserver = null;
    }

    const modal = getProductModal();
    if (!modal) return;

    galleryModalObserver = new MutationObserver(() => updateGalleryMode());
    galleryModalObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['open'],
    });

    updateGalleryMode();
  }

  function updatePagePadding(forceRemeasure = false) {
    const mobileMax = getMobileMaxWidth();
    const shouldReserveSpace =
      stickyBar &&
      isMobileViewport(mobileMax) &&
      stickyBar.classList.contains('is-visible') &&
      !menuOpen &&
      !galleryModalOpen;

    if (!shouldReserveSpace) {
      cachedBarHeight = 0;
      document.documentElement.classList.remove('plumelle-sticky-atc--active-mobile');
      return;
    }

    if (forceRemeasure || !cachedBarHeight) {
      cachedBarHeight = stickyBar.offsetHeight;
    }

    document.documentElement.style.setProperty('--plumelle-sticky-atc-height', `${cachedBarHeight}px`);
    document.documentElement.classList.add('plumelle-sticky-atc--active-mobile');
  }

  function setBarVisible(visible) {
    if (!stickyBar) return;

    if (menuOpen && visible) return;
    if (galleryModalOpen && !visible) return;

    stickyBar.classList.toggle('is-visible', visible);
    stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    updatePagePadding();
  }

  function clearRevealListeners() {
    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }

    if (scrollRevealHandler) {
      window.removeEventListener('scroll', scrollRevealHandler);
      document.removeEventListener('scroll', scrollRevealHandler, true);
      window.visualViewport?.removeEventListener('scroll', scrollRevealHandler);
      window.visualViewport?.removeEventListener('resize', scrollRevealHandler);
      scrollRevealHandler = null;
    }

    if (kachingRevealObserver) {
      kachingRevealObserver.disconnect();
      kachingRevealObserver = null;
    }

    if (primaryRebindTimer) {
      window.clearTimeout(primaryRebindTimer);
      primaryRebindTimer = null;
    }

    revealTarget = null;
    revealStrategy = null;
    primaryCtaTargets = [];
  }

  function getFastBundleRevealTarget() {
    return (
      document.querySelector('.rbr-page-container') ||
      document.querySelector('[class*="rbr-page-container"]') ||
      document
        .querySelector('[class*="rbr-standard-bundle"]')
        ?.closest('[class*="rbr-page-container"], [class*="CmmInlineWidget"]')
    );
  }

  function getBuyButtonsRevealTarget() {
    return (
      document.querySelector('.main-product-section .product__buy_buttons') ||
      document.querySelector('.product__buy_buttons')
    );
  }

  function isInsideStickyBar(el) {
    return Boolean(stickyBar && el && stickyBar.contains(el));
  }

  /**
   * Primary in-page CTAs: main buy buttons + Kaching ATCs (not the sticky bar).
   * Sticky should stay hidden until all of these have scrolled past the header.
   */
  function getPrimaryCtaTargets() {
    const found = [];
    const push = (el) => {
      if (!el || isInsideStickyBar(el) || found.includes(el)) return;
      found.push(el);
    };

    push(getBuyButtonsRevealTarget());

    document
      .querySelectorAll(
        [
          'kaching-bundles-block .product-form__submit',
          'kaching-bundles-block button[type="submit"]',
          'kaching-bundles-block button[name="add"]',
          'kaching-bundle .product-form__submit',
          'kaching-bundle button[type="submit"]',
          '.kaching-bundles .product-form__submit',
          '.kaching-bundles button[type="submit"]',
          '.kaching-bundles button[name="add"]',
          '.kaching-bundles [data-action="add-to-cart"]',
        ].join(', ')
      )
      .forEach(push);

    return found;
  }

  function getRevealTopOffset() {
    const header =
      document.querySelector('sticky-header') ||
      document.querySelector('.shopify-section-header-sticky') ||
      document.querySelector('#shopify-section-header');
    if (!header) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().height));
  }

  function shouldRevealAtTargetTop(target) {
    // Show once target top reaches / passes the header (legacy / always / fast-bundle modes).
    return target.getBoundingClientRect().top <= getRevealTopOffset();
  }

  /**
   * True when every primary CTA has fully scrolled above the sticky header.
   * CTAs still below the fold do NOT count as "past" (sticky stays hidden).
   * Zero-size / hidden nodes are ignored so expand/collapse remounts don't stick open.
   */
  function allPrimaryCtasScrolledPast() {
    const topOffset = getRevealTopOffset();
    const measurable = primaryCtaTargets.filter((el) => {
      if (!el.isConnected) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    if (!measurable.length) return false;

    return measurable.every((el) => el.getBoundingClientRect().bottom <= topOffset);
  }

  function getFooterRevealTarget() {
    return document.querySelector('footer.footer') || document.querySelector('.footer');
  }

  function shouldHideAtFooter() {
    const footer = getFooterRevealTarget();
    if (!footer) return false;
    // Hide once footer top reaches the bottom of the viewport
    return footer.getBoundingClientRect().top < window.innerHeight;
  }

  function updateRevealVisibility() {
    if (revealStrategy === 'past_primary') {
      const visible = allPrimaryCtasScrolledPast() && !shouldHideAtFooter();
      setBarVisible(visible);
      return;
    }

    if (!revealTarget) return;
    const visible = shouldRevealAtTargetTop(revealTarget) && !shouldHideAtFooter();
    setBarVisible(visible);
  }

  function attachScrollRevealHandlers() {
    scrollRevealHandler = () => updateRevealVisibility();

    // Capture-phase document scroll is more reliable on mobile Safari than window-only.
    window.addEventListener('scroll', scrollRevealHandler, { passive: true });
    document.addEventListener('scroll', scrollRevealHandler, { passive: true, capture: true });
    window.visualViewport?.addEventListener('scroll', scrollRevealHandler, { passive: true });
    window.visualViewport?.addEventListener('resize', scrollRevealHandler, { passive: true });
  }

  function observeRevealTargets(elements) {
    if (typeof IntersectionObserver === 'undefined') return;

    scrollObserver = new IntersectionObserver(() => updateRevealVisibility(), {
      threshold: [0, 0.01, 0.1, 0.25, 0.5, 1],
      rootMargin: `-${getRevealTopOffset()}px 0px 0px 0px`,
    });

    elements.forEach((el) => {
      if (el) scrollObserver.observe(el);
    });

    const footer = getFooterRevealTarget();
    if (footer) {
      scrollObserver.observe(footer);
    }
  }

  function primaryTargetsSignature(targets) {
    return targets.map((el) => `${el.tagName}:${el.className}:${el.id}`).join('|');
  }

  function rebindPrimaryCtaTargets() {
    if (revealStrategy !== 'past_primary') return;

    const next = getPrimaryCtaTargets();
    const stale = primaryCtaTargets.some((el) => !el.isConnected);
    if (
      !stale &&
      primaryTargetsSignature(next) === primaryTargetsSignature(primaryCtaTargets)
    ) {
      updateRevealVisibility();
      return;
    }

    primaryCtaTargets = next;

    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }

    observeRevealTargets(primaryCtaTargets);
    updateRevealVisibility();
  }

  function schedulePrimaryCtaRebind(delay = 120) {
    if (primaryRebindTimer) window.clearTimeout(primaryRebindTimer);
    primaryRebindTimer = window.setTimeout(() => {
      primaryRebindTimer = null;
      rebindPrimaryCtaTargets();
    }, delay);
  }

  function observeKachingForPrimaryRebind() {
    const root =
      document.querySelector('kaching-bundles-block') ||
      document.querySelector('kaching-bundle') ||
      document.querySelector('.kaching-bundles') ||
      document.querySelector('.main-product-section') ||
      document.querySelector('main');

    if (!root || typeof MutationObserver === 'undefined') return;

    kachingRevealObserver = new MutationObserver(() => schedulePrimaryCtaRebind(100));
    kachingRevealObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-expanded'],
    });
  }

  function attachTopRevealTarget(target) {
    if (!target) return;

    revealStrategy = 'top';
    revealTarget = target;
    attachScrollRevealHandlers();
    observeRevealTargets([target]);
    updateRevealVisibility();
  }

  /**
   * Default on_scroll: show sticky only after primary ATCs have left the viewport upward
   * (not while they are still on screen, and not while still below the fold).
   */
  function attachPastPrimaryReveal() {
    revealStrategy = 'past_primary';
    primaryCtaTargets = getPrimaryCtaTargets();
    attachScrollRevealHandlers();
    observeRevealTargets(primaryCtaTargets);
    observeKachingForPrimaryRebind();

    // Kaching mounts async — rebind after paint.
    schedulePrimaryCtaRebind(400);
    window.setTimeout(() => schedulePrimaryCtaRebind(0), 1200);

    updateRevealVisibility();
  }

  function startScrollReveal() {
    const targets = getPrimaryCtaTargets();
    if (!targets.length) {
      // No buy-buttons / Kaching target yet — footer-only gate via body top reveal.
      attachTopRevealTarget(document.body);
      // Retry past-primary once apps inject CTAs.
      window.setTimeout(() => {
        if (revealStrategy === 'top' && getPrimaryCtaTargets().length) {
          clearRevealListeners();
          attachPastPrimaryReveal();
        }
      }, 800);
      return;
    }

    attachPastPrimaryReveal();
  }

  function startFastBundleReveal() {
    const existingTarget = getFastBundleRevealTarget();
    if (existingTarget) {
      attachTopRevealTarget(existingTarget);
      return;
    }

    // Fast Bundle is disabled on PDP — fall back to primary-CTA past reveal.
    startScrollReveal();
  }

  function initRevealMode() {
    if (!stickyBar) return;

    const mobileMax = getMobileMaxWidth();
    const revealMode = stickyBar.dataset.revealMode || 'on_scroll';

    clearRevealListeners();

    if (!isMobileViewport(mobileMax)) {
      setBarVisible(false);
      return;
    }

    if (revealMode === 'always') {
      // Always visible on mobile, but still hide when footer enters the viewport.
      attachTopRevealTarget(document.body);
      return;
    }

    if (revealMode === 'on_fast_bundle') {
      startFastBundleReveal();
      return;
    }

    startScrollReveal();
  }

  function onVariantChange(event) {
    if (syncing) return;

    const fromSectionId = event.detail?.sectionId;
    const variant = event.detail?.variant;

    if (!fromSectionId || !variant || !mainSectionId || !stickySectionId) return;
    if (fromSectionId !== mainSectionId && fromSectionId !== stickySectionId) return;

    syncing = true;

    try {
      if (fromSectionId === stickySectionId) {
        syncPickerFromVariant(mainSectionId, variant, true);
        applyStickyUpdates(variant, { skipColorSync: true });
        scheduleStickySyncFromMain(variant);
      } else {
        applyStickyUpdates(variant);
        syncStickyAvailabilityFromMain();
      }
    } finally {
      syncing = false;
    }

    if (fromSectionId === stickySectionId) {
      scheduleRubikSyncFromMain();
    }
  }

  function onResize() {
    cachedBarHeight = 0;
    updateGalleryMode();
    initRevealMode();
    updatePagePadding(true);
    if (galleryModalOpen) {
      updateGalleryModalPadding(true);
    }
  }

  function init() {
    stickyBar = getStickyBar();
    mainSectionId = getMainSectionId();
    stickySectionId = getStickySectionId();

    if (!stickyBar || !mainSectionId || !stickySectionId) return;

    const mountWithRetry = (attempt = 0) => {
      mountVariantPicker();
      linkStickyPickerToMain();
      repairStickyPicker();

      if (!document.getElementById(`variant-radios-${stickySectionId}`) && attempt < 15) {
        window.setTimeout(() => mountWithRetry(attempt + 1), 100);
        return;
      }

      syncColorSwatchesFromMain();
      syncRubikSwatchStyles();
      const mainPicker = document.getElementById(`variant-radios-${mainSectionId}`);
      if (mainPicker?.currentVariant) {
        syncStickyColorName(mainPicker.currentVariant);
      }
      window.setTimeout(() => {
        syncColorSwatchesFromMain();
        syncRubikSwatchStyles();
        const picker = document.getElementById(`variant-radios-${mainSectionId}`);
        if (picker?.currentVariant) {
          syncStickyColorName(picker.currentVariant);
        }
      }, 300);
      window.setTimeout(() => {
        syncColorSwatchesFromMain();
        syncRubikSwatchStyles();
        const picker = document.getElementById(`variant-radios-${mainSectionId}`);
        if (picker?.currentVariant) {
          syncStickyColorName(picker.currentVariant);
        }
      }, 1000);
      observeRubikReady();
      observeStickyPickerRepairs();
    };

    mountWithRetry();
    observeRubikReady();
    initRevealMode();
    observeMenuState();
    observeGalleryModal();

    document.addEventListener('variant:change', onVariantChange);
    document.body.addEventListener('modalClosed', onModalClosed);
    window.addEventListener('resize', onResize, { passive: true });

    requestAnimationFrame(() => updatePagePadding(true));
  }

  function teardown() {
    document.removeEventListener('variant:change', onVariantChange);
    document.body.removeEventListener('modalClosed', onModalClosed);
    window.removeEventListener('resize', onResize);

    clearRevealListeners();

    if (galleryModalObserver) {
      galleryModalObserver.disconnect();
      galleryModalObserver = null;
    }

    if (menuObserver) {
      menuObserver.disconnect();
      menuObserver = null;
    }

    if (pickerObserver) {
      pickerObserver.disconnect();
      pickerObserver = null;
    }

    if (rubikObserver) {
      rubikObserver.disconnect();
      rubikObserver = null;
    }

    menuOpen = false;
    wasVisibleBeforeMenu = false;
    galleryModalOpen = false;

    stickyBar?.classList.remove('is-gallery-mode');
    document.body.classList.remove('plumelle-gallery-atc-active');
    clearGalleryModalPadding();

    document.documentElement.classList.remove('plumelle-sticky-atc--active-mobile');
    document.documentElement.style.removeProperty('--plumelle-sticky-atc-height');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    teardown();
    stickyBar = null;
    mainSectionId = null;
    stickySectionId = null;
    init();
  });
})();